import { Location } from '../../../models/locations-models/location';
import { getHexCode } from '../../../core/utilities';
import { isDevMode } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { CCLocation } from '../../../models/locations-models/cc-location';
import { isCcCandidate } from '../location-utils';
import { Coordinate } from '../../../models/coordinate';
import { isEqual, isNumber } from 'lodash';
import { environment } from '../../../../environments/environment';
import { AUTOCOMPLETE_MARKER_ID, VALIDATED_MARKER_ID } from '../constants';
import { round } from '@turf/helpers';
import { CoordinateSource } from '../../../models/coordinate-history';

export enum CvMarkerType {
  IMPORT = 'IMPORT',
  GEOCODE = 'GEOCODE',
  GBP = 'GBP', // accepted GBP coordinate diff
  CUSTOM = 'CUSTOM',
  GROUP = 'GROUP',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  HISTORY = 'HISTORY'
}

export enum CvMarkerAction {
  VALIDATE = 'Validate',
  REVALIDATE = 'Revalidate',
  INVALIDATE = 'Invalidate'
}

export const MARKER_COLORS = {
  LIME: getHexCode('green-70'),
  DARK_BLUE: getHexCode('blue-20'),
  BLUE: getHexCode('blue-50'),
  GREY: getHexCode('neutral-60'),
  LIGHT_BLUE: getHexCode('blue-60'),
  ORANGE: getHexCode('orange-20'),
  RED: getHexCode('red-50'),
};

export const CENTER_OF_EUROPE = {
  lat: 54.526,
  lng: 15.255,
};

export const INITIAL_ZOOM = 4;
export const MAX_ZOOM = 21;

// Map-IDs and associated Map Styles are managed directly in GCP
// https://console.cloud.google.com/google/maps-apis/studio/maps
export const MAP_ID_STYLING = '6d6ba7ce1e00da88';

// !! Needs the Google Maps API to be loaded first in order to be able to access google.maps.XX !!
export const MAP_OPTIONS: (cv?: boolean) => google.maps.MapOptions = (cv = false) => {
  return {
    center: CENTER_OF_EUROPE,
    zoom: INITIAL_ZOOM,
    mapId: MAP_ID_STYLING,
    mapTypeControl: true,
    mapTypeControlOptions: {
      position: google.maps.ControlPosition.BOTTOM_CENTER,
      mapTypeIds: [google.maps.MapTypeId.ROADMAP,
        google.maps.MapTypeId.HYBRID,
        google.maps.MapTypeId.SATELLITE],
    },
    fullscreenControl: true,
    fullscreenControlOptions: {
      position: google.maps.ControlPosition.BOTTOM_LEFT,
    },
    cameraControl: false,
    zoomControl: true,
    streetViewControl: cv,
  };
};

export const CIRCLE_OPTIONS = (): google.maps.CircleOptions => {
  return {
    strokeColor: MARKER_COLORS.LIGHT_BLUE,
    strokeOpacity: 0.64,
    strokeWeight: 2,
    fillColor: MARKER_COLORS.LIGHT_BLUE,
    fillOpacity: 0.16,
    clickable: false, // allows interaction with POIs
  };
};

export const STREET_VIEW_OPTIONS: (cvOn: boolean) => google.maps.StreetViewPanoramaOptions = (cvOn) => ({
  visible: cvOn, // show street view by default if in CV mode
  motionTracking: false, // causes havoc on motion-sensitive devices, fixes BLS-4429
  motionTrackingControl: false,
  pov: {
    heading: 0, // point north by default
    pitch: 0, // horizontal by default
  },
  clickToGo: true,
  addressControl: false, // this is too distracting
  enableCloseButton: false, // we'll handle closing on our own
  linksControl: false,
  fullscreenControl: false,
  imageDateControl: true,
});

export interface MapMarkerData {
  id: string | CvMarkerType;
  type: CvMarkerType,
  position: google.maps.LatLngLiteral;
  overlappingMarkers?: MapMarkerData[];
}

export interface DefaultMapMarkerOptions extends google.maps.MarkerOptions {
  iconData?: {
    default: string,
    label: google.maps.MarkerLabel,
    minimized: string,
  };
}

export interface DefaultMapMarker {
  id: string; // String(position.lat) + String(position.lng)
  markerLocations: MarkerLocation[];
  position: google.maps.LatLngLiteral;
  options: DefaultMapMarkerOptions;
}

export interface AutocompleteMarker {
  id: string, // AUTOCOMPLETE_MARKER_ID
  position: google.maps.LatLngLiteral;
  options: google.maps.MarkerOptions; // icon
}

export interface MarkerLocation {
  location: Location | CCLocation;
  label: google.maps.MarkerLabel,
  url: {
    default: string;
    minimized: string;
  };
}

export const getAutocompleteMarkerUrl = (minimize: boolean): string => {
  const url = ['assets/images/mapMarkers/autocomplete_marker.png', 'assets/images/mapMarkers/autocomplete_marker_minimized.png'];
  return minimize ? url[1] : url[0];
};

export const getMarkerUrl = (location: Location | CCLocation): MarkerLocation['url'] => {
  // returns the right url for single-color marker SVG
  const getMarkerIconUrls = () => {
    return !isCcCandidate(location) && !location.foundInMyBusiness && !dualColorMarker ? ['assets/images/mapMarkers/lime_marker.png', 'assets/images/mapMarkers/lime_marker_minimized' +
        '.png'] // missing LSC location
        : !isCcCandidate(location) && !location.verified && !dualColorMarker ? ['assets/images/mapMarkers/grey_marker.png', 'assets/images/mapMarkers/grey_marker_minimized.png'] // unvalidated LSC location
            : !isCcCandidate(location) && location.businessStatus === 'CLOSED_PERMANENTLY' ? ['assets/images/mapMarkers/red_marker.png', 'assets/images/mapMarkers/red_marker_minimized.png'] // perm. closed LSC location
                : !isCcCandidate(location) ? ['assets/images/mapMarkers/dark_blue_marker.png', 'assets/images/mapMarkers/dark_blue_marker_minimized.png'] // regular LSC location
                    : isCcCandidate(location) && (location as CCLocation).isMarkedAsDuplicate ? ['assets/images/mapMarkers/orange_marker.png', 'assets/images/mapMarkers/orange_marker_minimized.png'] // duplicate candidate
                        : ['assets/images/mapMarkers/light_blue_marker.png', 'assets/images/mapMarkers/light_blue_marker_minimized.png']; // regular candidate
  };


  const dualColorMarker = !isCcCandidate(location) && !location.verified && !location.foundInMyBusiness; // unvalidated and missing LSC location

  if (dualColorMarker) {
    return {
      default: 'assets/images/mapMarkers/grey_and_lime_marker.png',
      minimized: 'assets/images/mapMarkers/grey_and_lime_marker_minimized.png',
    };
  }

  return {
    default: getMarkerIconUrls()[0],
    minimized: getMarkerIconUrls()[1],
  };
};


export const getCvMarkerUrl = (markerType: CvMarkerType | CoordinateSource, verified: boolean, minimized: boolean): string => {
  const url = verified && markerType === CvMarkerType.GEOCODE ? ['assets/images/mapMarkers/cv_marker_geocoded_verified.png', 'assets/images/mapMarkers/blue_marker_minimized.png'] :
      verified && markerType === CvMarkerType.GBP ? ['assets/images/mapMarkers/cv_marker_gbp_verified.png', 'assets/images/mapMarkers/blue_marker_minimized.png'] :
          verified && markerType === CvMarkerType.IMPORT ? ['assets/images/mapMarkers/cv_marker_imported_verified.png', 'assets/images/mapMarkers/blue_marker_minimized.png'] :
              verified && markerType === CvMarkerType.CUSTOM ? ['assets/images/mapMarkers/cv_marker_custom_verified.png', 'assets/images/mapMarkers/blue_marker_minimized.png'] :
                  markerType === CvMarkerType.GEOCODE ? ['assets/images/mapMarkers/cv_marker_geocoded.png', 'assets/images/mapMarkers/grey_marker_minimized.png'] :
                      markerType === CvMarkerType.GBP ? ['assets/images/mapMarkers/cv_marker_gbp.png', 'assets/images/mapMarkers/grey_marker_minimized.png'] :
                          markerType === CvMarkerType.IMPORT ? ['assets/images/mapMarkers/cv_marker_imported.png', 'assets/images/mapMarkers/grey_marker_minimized.png'] :
                              markerType === CvMarkerType.GROUP ? ['assets/images/mapMarkers/multi_marker_icon.png', 'assets/images/mapMarkers/multi_marker_icon_minimized.png'] :
                                  markerType === CvMarkerType.AUTOCOMPLETE ? ['assets/images/mapMarkers/autocomplete_marker.png', 'assets/images/mapMarkers/autocomplete_marker_minimized.png'] :
                                      ['assets/images/mapMarkers/cv_marker_custom.png', 'assets/images/mapMarkers/grey_marker_minimized.png'];
  return minimized ? url[1] : url[0];
};

/**
 * A verified marker should represent the verified coordinates of the focused location. There can
 * only be one, and it's ID should equal VALIDATED_MARKER_ID
 * @param markerId
 */
export const isVerifiedMarker = (markerId: string) => {
  return markerId === VALIDATED_MARKER_ID;
};

/**
 * Returns coordinate field that map markers should use as their geospatial position to be rendered at
 * TODO: refactor to a ngrx selector in BLS-4279
 */
export const getCoordinateField = () => {
  // for NOW, use coordinateImport for DEV environment (when also running BE locally) as we don't
  // sync coordinates there
  return isDevMode() && environment.baseURL === 'http://localhost:8080' ? 'coordinateImport' : 'coordinate';
};

/**
 * Convert latLng to xy position
 * Taken from https://jsfiddle.net/svigna/VzYF6/
 * @param mapInstance
 * @param latLng
 */
export const convertLatLngToPoint = (mapInstance: GoogleMap,
                                     latLng: google.maps.LatLng | google.maps.LatLngLiteral): google.maps.Point => {
  const topRight = mapInstance.getProjection().fromLatLngToPoint(mapInstance.getBounds().getNorthEast());
  const bottomLeft = mapInstance.getProjection().fromLatLngToPoint(mapInstance.getBounds().getSouthWest());
  const scale = Math.pow(2, mapInstance.getZoom());
  const worldPoint = mapInstance.getProjection().fromLatLngToPoint(latLng);
  return new google.maps.Point((worldPoint.x - bottomLeft.x) * scale, (worldPoint.y - topRight.y) * scale);
};

/**
 * Ensures parsable `latitude` and `longitude` object members.
 * @param coords
 */
export const areCoordsValid = (coords?: Coordinate) => {
  if (typeof coords !== 'undefined'
      && isNumber(coords?.latitude)
      && isNumber(coords?.longitude)) {
    return true;
  } else {
    console.warn(`INVALID COORDINATES: ${coords}`);
    return false;
  }
};

/**
 * Conversion helper
 * @param position
 */
export const convertToCoordinateType = (position: google.maps.LatLngLiteral): Coordinate => {
  return {
    latitude: position?.lat,
    longitude: position?.lng,
  };
};

/**
 * Given a panorama center, calculate the heading (the camera heading in degrees relative to true north -- 0°)
 * and the pitch (relative to the street view vehicle) so that the streetview "looks"
 * in the direction of a given `targetPosition`.
 * @param panoramaBaselineCenter
 * @param targetPosition
 */
export const getOptimizedPanoramaView = (
    panoramaBaselineCenter: google.maps.LatLng,
    targetPosition: google.maps.LatLng,
): Pick<google.maps.StreetViewPanoramaOptions, 'pov' | 'position'> => {
  return {
    position: panoramaBaselineCenter,
    pov: {
      heading: google.maps.geometry.spherical.computeHeading(
          panoramaBaselineCenter,
          targetPosition,
      ),
      // -5° because the `targetPosition`'s pin tends to be "on the ground"
      // and so if we want to see it, we need to "look down" a bit.
      pitch: -5,
    },
  };
};

/**
 * Generate Autocomplete marker
 * @param minimize
 * @param position
 * @private
 */
export const generateAutocompleteMarker = (
    minimize: boolean,
    position?: google.maps.LatLngLiteral): AutocompleteMarker => {
  return {
    id: AUTOCOMPLETE_MARKER_ID,
    position,
    options: {
      icon: {
        url: getAutocompleteMarkerUrl(minimize),
      },
      zIndex: 99,
    },
  };
};

/**
 * Creates G / I Markers based on currently focused location
 * @param focusedLocation
 * @param roundPrecision
 */
export const createLocationMarkers = (focusedLocation: Location, roundPrecision = 5) => {
  if (!focusedLocation) return [];

  return [CvMarkerType.GEOCODE, CvMarkerType.IMPORT]
      .filter(t => areCoordsValid(t === CvMarkerType.IMPORT ?
          focusedLocation.coordinateImport :
          focusedLocation.coordinate))
      .map(t => ({
        id: focusedLocation.verified && t === CvMarkerType.GEOCODE ? VALIDATED_MARKER_ID : String(t),
        type: t,
        position: {
          lat: round(t === CvMarkerType.GEOCODE ?
              focusedLocation.coordinate.latitude :
              focusedLocation.coordinateImport.latitude, roundPrecision),
          lng: round(t === CvMarkerType.GEOCODE ?
              focusedLocation.coordinate.longitude :
              focusedLocation.coordinateImport.longitude, roundPrecision),
        },
      }));
};

/**
 * Determine marker action
 * @param marker
 * @param isVerified
 */
export const determineMarkerAction = (marker: MapMarkerData, isVerified: boolean): CvMarkerAction => {
  if (!marker) {
    return;
  }

  if (isVerified) {
    if (marker.id === VALIDATED_MARKER_ID) {
      return CvMarkerAction.INVALIDATE;
    } else return CvMarkerAction.REVALIDATE;
  } else return CvMarkerAction.VALIDATE;
};

/**
 * Returns true if the amount of markers or their positions have not changed
 * @param a
 * @param b
 * @private
 */
export const markersComparator = (
    a: DefaultMapMarker[] | MapMarkerData[],
    b: DefaultMapMarker[] | MapMarkerData[]): boolean => {
  const positionsA = a.map(m => m.position);
  const positionsB = b.map(m => m.position);

  return !a.length && !b.length || // there were and are no markers ...
      a.length === b.length && a.length >= 1 && // same amount of markers, >= 1
      positionsA.every((position, i) => isEqual(position, positionsB[i])); // positions have not changed
};
